import HalfEdgeMesh from '@/modules/three/common/halfEdgeMesh/HalfEdgeMesh.js';
import Gizmo from '@modules/three/common/transformMesh/Gizmo.ts';
import { BufferGeometry, Euler, Mesh, MeshBasicMaterial, Object3D, Quaternion, SphereGeometry, Vector3 } from 'three';
import { threeStore } from '@modules/three/store/threeStore.ts';
import { DEG2RAD } from '@constants/MathGeometry';

export default class TransformMesh extends HalfEdgeMesh {
  protected gizmo: Gizmo;
  protected pivot = new Object3D();
  private startPivotQuat = new Quaternion();
  private currPivotQuat = new Quaternion();

  protected initialMeshQuat = new Quaternion();
  protected initialPosition: Vector3 = new Vector3();
  protected initialRotation: Euler = new Euler();

  protected translateLimit = 3.0;
  protected rotateLimitDeg = 43;

  private onObjectChange = () => {};
  private onMouseDown = () => {};
  private onMouseUp = () => {};

  constructor(geometry: BufferGeometry) {
    super(geometry);
    this.gizmo = threeStore.getState().rootState.gizmo;
    this.pivot.add(new Mesh(new SphereGeometry(0), new MeshBasicMaterial({ color: 0xff0000 })));
    this.rootState.scene.add(this.pivot);
  }

  protected setPivotTransform(position: Vector3 | null, rotation: Vector3 | null) {
    if (position) {
      this.pivot.position.copy(position);
    }
    if (rotation && position) {
      const rotationVector = new Vector3().subVectors(rotation, position).normalize();
      const quaternion = new Quaternion().setFromUnitVectors(new Vector3(0, 1, 0), rotationVector);
      this.pivot.quaternion.copy(quaternion); // VS multiply()?
    } else {
      this.pivot.quaternion.set(0, 0, 0, 0);
    }

    this.pivot.getWorldQuaternion(this.startPivotQuat);
    this.initialMeshQuat.copy(this.quaternion);

    this.initialPosition = this.pivot.position.clone();
    this.initialRotation = this.pivot.rotation.clone();
  }

  /**
   * Re-orients the gizmo pivot (and therefore the local-space gizmo handles)
   * without touching the mesh baseline (`initialMeshQuat`/`initialPosition`).
   * Init-only: once a drag has accumulated a delta, overwriting the pivot here
   * would collapse that delta and the next drag would snap the mesh back —
   * after interaction use {@link syncPivotToMeshTransform} instead.
   */
  protected setPivotOrientation(quaternion: Quaternion) {
    this.pivot.quaternion.copy(quaternion);
    this.pivot.getWorldQuaternion(this.startPivotQuat);
    this.initialRotation = this.pivot.rotation.clone();
  }

  /**
   * After a programmatic mesh transform, realigns the pivot so the next drag
   * starts from the mesh's actual pose: inverts rotateMeshAlongGizmo's
   * delta math (delta = pivotWorld × inv(startPivotQuat), applied to
   * initialMeshQuat). The pivot is a scene-root child, so world == local.
   */
  protected syncPivotToMeshTransform() {
    this.pivot.position.copy(this.position);
    this.pivot.quaternion
      .copy(this.quaternion)
      .multiply(this.initialMeshQuat.clone().invert())
      .multiply(this.startPivotQuat);
  }

  public detach() {
    this.gizmo.detach();
    this.gizmo.removeEventListener('objectChange', this.onObjectChange);
    this.gizmo.removeEventListener('mouseDown', this.onMouseDown);
    this.gizmo.removeEventListener('mouseUp', this.onMouseUp);
  }

  protected attachGizmo(callback: () => void) {
    this.gizmo.attachToParent(this.pivot, callback);

    this.onObjectChange = () => {
      threeStore.getState().rootState.controls.enabled = false;
      if (this.gizmo.mode === 'translate') {
        this.translateMeshAlongGizmo();
      } else {
        this.rotateMeshAlongGizmo();
      }
      callback();
      threeStore.getState().rootState.controls.enabled = true;
    };
    this.gizmo.addEventListener('objectChange', this.onObjectChange);

    type JawWithContact = {
      clearContactOverlay: () => void;
      updateGumVerticesAttachedToTooth: (t: TransformMesh) => void;
      onActiveToothTransformEnd: (t: TransformMesh) => void;
      getJawsCorrection: () => { activeTooth: TransformMesh | null };
    };

    this.onMouseDown = () => {
      const jaw = (this as { jaw?: JawWithContact }).jaw;
      jaw?.clearContactOverlay();
    };
    this.gizmo.addEventListener('mouseDown', this.onMouseDown);

    this.onMouseUp = () => {
      const jaw = (this as { jaw?: JawWithContact }).jaw;
      const t = jaw?.getJawsCorrection().activeTooth;
      if (t && jaw) {
        jaw.updateGumVerticesAttachedToTooth(t);
        t.geometry.computeBoundingBox();
        t.geometry.computeBoundsTree();
        t.geometry.boundsTree?.refit();
        jaw.onActiveToothTransformEnd(t);
      }
    };
    this.gizmo.addEventListener('mouseUp', this.onMouseUp);
  }

  private translateMeshAlongGizmo() {
    if (this.pivot.position.distanceTo(this.getInitialPosition()) > this.translateLimit) {
      this.pivot.position.copy(this.position);
      return;
    }
    this.setPosition(this.pivot.position);
  }

  private rotateMeshAlongGizmo() {
    // compute Δ = curr × inv(start)
    this.pivot.getWorldQuaternion(this.currPivotQuat);
    const deltaQuat = this.currPivotQuat.clone().multiply(this.startPivotQuat.clone().invert()).normalize();

    // **normalize** to prevent drift → no NaNs (mesh growth) and axis-coupling
    const newMeshQuat = deltaQuat.multiply(this.initialMeshQuat).normalize();

    const e = new Euler().setFromQuaternion(newMeshQuat, 'XYZ');
    const max = this.rotateLimitDeg * DEG2RAD;
    e.x = Math.max(-max, Math.min(max, e.x));
    e.y = Math.max(-max, Math.min(max, e.y));
    e.z = Math.max(-max, Math.min(max, e.z));
    const clampedQuat = new Quaternion().setFromEuler(e);

    this.setRotationFromQuaternion(clampedQuat);
    this.rotateHalfEdges(clampedQuat, this.position);
  }

  public resetPosition() {
    this.setPosition(this.initialPosition);
    this.pivot.position.copy(this.initialPosition);
    /* explained why not working for future
    //this.initialMeshQuat.copy(this.quaternion); // not reset gizmo, next tooth click breaks position!
    //this.pivot.getWorldQuaternion(this.startPivotQuat); // not reset gizmo, next tooth click breaks position!
    //this.pivot.quaternion.copy(this.quaternion); // unwanted gizmo rotation centralAxis here, next tooth click breaks position!
    */
  }

  public resetRotation() {
    this.setRotationFromQuaternion(this.initialMeshQuat);
    this.rotateHalfEdges(this.initialMeshQuat, this.getPivotLocation());
    this.pivot.rotation.copy(this.initialRotation);
    /* explained why not working for future
    //this.initialMeshQuat.copy(this.quaternion); // not want gizmo same rot + next tooth click breaks rotation
    //this.pivot.getWorldQuaternion(this.startPivotQuat); // not want gizmo same rot + next tooth click works well
    //this.pivot.quaternion.copy(this.quaternion); // wrong gizmo rot + next tooth click breaks rotation
    */
  }

  public getInitialPosition(): Vector3 {
    return this.initialPosition;
  }

  public getInitialQuaternion(): Quaternion {
    return this.initialMeshQuat.clone();
  }

  public getPivotLocation(): Vector3 {
    return this.pivot.position;
  }
}
